import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { buildApiUrl, getApiBaseUrl } from '../../config/apiConfig';
import './MyCourseContentPage.css';
import { logError } from '../../utils/logger';

const resolveImageUrl = (source) => {
  if (!source) return null;
  const sanitized = String(source).replace(/\\/g, '/').replace(/\s/g, '%20');
  if (/^https?:\/\//i.test(sanitized)) return sanitized;
  const base = getApiBaseUrl();
  if (sanitized.startsWith('/')) return `${base}${sanitized}`;
  if (sanitized.startsWith('uploads/')) return `${base}/${sanitized}`;
  return `${base}/uploads/${sanitized}`;
};

/** El backend puede enviar puntaje como string (p. ej. DECIMAL de MySQL). */
const formatExamPuntajePercent = (puntaje) => {
  const n = Number(puntaje);
  return Number.isFinite(n) ? n.toFixed(0) : '0';
};

const MyCourseContentPage = () => {
  const { courseId } = useParams();
  const { user, isAuthenticated, token } = useAuth();
  const { notify } = useNotifications();
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedModules, setExpandedModules] = useState({});
  const [exams, setExams] = useState([]);
  const [loadingExams, setLoadingExams] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      notify({
        message: 'Debes iniciar sesión para ver el contenido del curso.',
        type: 'info',
      });
      navigate('/login', { state: { from: `/my-courses/${courseId}/content` } });
      return;
    }

    fetchCourseContent();
  }, [courseId, isAuthenticated, token, navigate, notify]);

  const fetchCourseContent = async () => {
    setLoading(true);
    setError(null);

    try {
      // Verificar que el usuario tiene acceso al curso
      const checkResponse = await fetch(buildApiUrl(`/api/user/check-course/${courseId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!checkResponse.ok) {
        const errorData = await checkResponse.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.message || 'No tienes acceso a este curso');
      }

      const checkData = await checkResponse.json();
      if (!checkData.hasCourse) {
        notify({
          message: 'No tienes acceso a este curso. Puedes adquirirlo en la tienda.',
          type: 'warning',
        });
        navigate('/courses');
        return;
      }

      // Obtener el curso completo con módulos y clases
      const courseResponse = await fetch(buildApiUrl(`/api/courses/${courseId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!courseResponse.ok) {
        const errorData = await courseResponse.json().catch(() => ({ message: 'Error desconocido' }));
        throw new Error(errorData.message || `Error al cargar el curso (${courseResponse.status})`);
      }

      const courseData = await courseResponse.json();
      setCourse(courseData.course || courseData);

      // Obtener el progreso del usuario
      const progressResponse = await fetch(buildApiUrl(`/api/user/courses/progress/${courseId}`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (progressResponse.ok) {
        const progressData = await progressResponse.json();
        setProgress(progressData);
        
        // Si el curso está al 100%, obtener los exámenes disponibles
        const progressPercent = progressData.progressPercent || 0;
        const isCompleted = progressData.completed || progressPercent >= 100;
        
        if (isCompleted) {
          fetchCourseExams();
        }
      }
    } catch (err) {
      logError('[MyCourseContentPage] Error fetching course content:', err);
      setError(err.message || 'Error al cargar el contenido del curso.');
      notify({
        message: err.message || 'Error al cargar el contenido del curso.',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const toggleModule = (moduleId) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleId]: !prev[moduleId],
    }));
  };

  const handleClassClick = (clase) => {
    navigate(`/my-courses/${courseId}/class/${clase.id}`);
  };

  const fetchCourseExams = async () => {
    setLoadingExams(true);
    try {
      const examsResponse = await fetch(buildApiUrl(`/api/exams/courses/${courseId}/exams/user`), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (examsResponse.ok) {
        const examsData = await examsResponse.json();
        setExams(examsData.exams || []);
      }
    } catch (err) {
      logError('[MyCourseContentPage] Error fetching exams:', err);
    } finally {
      setLoadingExams(false);
    }
  };

  const handleTakeExam = (examId) => {
    navigate(`/my-courses/${courseId}/exam/${examId}`);
  };

  const calculateTotalMinutes = () => {
    if (!course?.modulos) return 0;
    return course.modulos.reduce((total, modulo) => {
      const moduleMinutes = modulo.clases?.reduce((sum, clase) => sum + (clase.minutes || 0), 0) || 0;
      return total + moduleMinutes;
    }, 0);
  };

  const calculateTotalClasses = () => {
    if (!course?.modulos) return 0;
    return course.modulos.reduce((total, modulo) => total + (modulo.clases?.length || 0), 0);
  };

  const isClassCompleted = (classId) => {
    return progress?.completedClasses?.includes(classId) || false;
  };

  if (loading) {
    return (
      <div className="my-course-content-page loading-state">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Cargando contenido del curso...</span>
        </div>
        <p>Cargando contenido del curso...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="my-course-content-page error-state">
        <div className="alert alert-danger" role="alert">
          <i className="fas fa-exclamation-circle me-2"></i>
          {error}
        </div>
        <button className="btn btn-primary mt-3" onClick={() => navigate('/my-courses')}>
          <i className="fas fa-arrow-left me-2"></i>
          Volver a Mis Cursos
        </button>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="my-course-content-page error-state">
        <div className="alert alert-warning" role="alert">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Curso no encontrado
        </div>
        <button className="btn btn-primary mt-3" onClick={() => navigate('/my-courses')}>
          <i className="fas fa-arrow-left me-2"></i>
          Volver a Mis Cursos
        </button>
      </div>
    );
  }

  const imageUrl = resolveImageUrl(course.imagen);
  const totalMinutes = calculateTotalMinutes();
  const totalHours = Math.round((totalMinutes / 60) * 10) / 10;
  const totalClasses = calculateTotalClasses();
  const progressPercent = progress?.progressPercent || 0;
  const completedClassesCount = progress?.completedClassesCount || progress?.completedClasses?.length || 0;
  const isCourseCompleted = progress?.completed || progressPercent >= 100;

  return (
    <div className="my-course-content-page">
      {/* Hero Header del Curso */}
      <section className="course-content-hero">
        <div className="course-content-hero-background">
          <div className="course-content-hero-overlay"></div>
        </div>
        <div className="course-content-hero-content">
          <button className="course-content-back-button" onClick={() => navigate('/my-courses')}>
            <i className="fas fa-arrow-left"></i>
            <span>Volver a Mis Cursos</span>
          </button>
          
          <div className="course-content-hero-main">
            <div className="course-content-hero-image-wrapper">
              {imageUrl ? (
                <div className="course-content-hero-image">
                  <img src={imageUrl} alt={course.titulo || course.nombre} />
                </div>
              ) : (
                <div className="course-content-hero-image-placeholder">
                  <i className="fas fa-graduation-cap"></i>
                </div>
              )}
            </div>
            
            <div className="course-content-hero-info">
              <div className="course-content-hero-badge">
                <span>CONTENIDO DEL CURSO</span>
              </div>
              <h1 className="course-content-hero-title">
                {course.titulo || course.nombre}
              </h1>
              {course.descripcionBreve && (
                <p className="course-content-hero-description">
                  {course.descripcionBreve}
                </p>
              )}
              
              <div className="course-content-hero-stats">
                <div className="course-stat-card">
                  <i className="fas fa-folder"></i>
                  <div>
                    <span className="stat-value">{course.modulos?.length || 0}</span>
                    <span className="stat-label">{course.modulos?.length === 1 ? 'Módulo' : 'Módulos'}</span>
                  </div>
                </div>
                <div className="course-stat-card">
                  <i className="fas fa-play-circle"></i>
                  <div>
                    <span className="stat-value">{totalClasses}</span>
                    <span className="stat-label">{totalClasses === 1 ? 'Clase' : 'Clases'}</span>
                  </div>
                </div>
                <div className="course-stat-card">
                  <i className="fas fa-clock"></i>
                  <div>
                    <span className="stat-value">{totalHours}h</span>
                    <span className="stat-label">de contenido</span>
                  </div>
                </div>
              </div>
              
              {progress && (
                <div className="course-content-hero-progress">
                  <div className="course-progress-header">
                    <span className="course-progress-label">Tu Progreso</span>
                    <span className="course-progress-percent">{progressPercent.toFixed(0)}%</span>
                  </div>
                  <div className="course-progress-bar-wrapper">
                    <div
                      className="course-progress-bar-fill"
                      style={{ width: `${progressPercent}%` }}
                    ></div>
                  </div>
                  <div className="course-progress-details">
                    <span className="course-progress-text">
                      <i className="fas fa-check-circle"></i>
                      {completedClassesCount} de {totalClasses} clases completadas
                    </span>
                    {isCourseCompleted && (
                      <div className="course-completed-badge-hero">
                        <i className="fas fa-trophy"></i>
                        <span>¡Curso Completado!</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Examen del Curso - Solo si está completado */}
      {isCourseCompleted && exams.length > 0 && (
        <section className="course-exam-section">
          <div className="course-content-container">
            <div className="course-exam-card">
              <div className="course-exam-header">
                <div className="course-exam-icon-wrapper">
                  <i className="fas fa-clipboard-check"></i>
                </div>
                <div>
                  <div className="course-exam-badge">
                    <span>EXAMEN DISPONIBLE</span>
                  </div>
                  <h2 className="course-exam-title">Examen del Curso</h2>
                </div>
              </div>
              <p className="course-exam-description">
                Has completado todas las clases. Ahora puedes realizar el examen para obtener tu certificado.
              </p>
              <div className="course-exam-list">
                {exams.map((exam) => (
                  <div key={exam.id} className="course-exam-item">
                    <div className="exam-item-info">
                      <h3>{exam.titulo}</h3>
                      {exam.descripcion && <p>{exam.descripcion}</p>}
                      <div className="exam-item-meta">
                        <span>
                          <i className="fas fa-question-circle me-1"></i>
                          {exam.preguntasCount} {exam.preguntasCount === 1 ? 'pregunta' : 'preguntas'}
                        </span>
                        {exam.userResult && (
                          <span className={`exam-result-badge ${exam.userResult.aprobado ? 'approved' : 'failed'}`}>
                            <i className={`fas fa-${exam.userResult.aprobado ? 'check' : 'times'}-circle me-1`}></i>
                            {formatExamPuntajePercent(exam.userResult.puntaje)}% - {exam.userResult.aprobado ? 'Aprobado' : 'Reprobado'}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      className="btn btn-primary exam-take-button"
                      onClick={() => handleTakeExam(exam.id)}
                    >
                      {exam.userResult ? (
                        <>
                          <i className="fas fa-redo me-2"></i>
                          Ver Resultado
                        </>
                      ) : (
                        <>
                          <i className="fas fa-play me-2"></i>
                          Realizar Examen
                        </>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Contenido del Curso - Módulos y Clases */}
      <section className="course-content-body">
        <div className="course-content-container">
          <div className="course-content-section-header">
            <div className="course-content-section-title-wrapper">
              <i className="fas fa-list"></i>
              <h2 className="course-content-section-title">Contenido del Curso</h2>
            </div>
            {progress && (
              <div className="course-content-section-progress">
                <span>Progreso General: {progressPercent.toFixed(0)}%</span>
              </div>
            )}
          </div>

          {!course.modulos || course.modulos.length === 0 ? (
            <div className="course-content-empty">
              <i className="fas fa-inbox"></i>
              <p>Este curso aún no tiene contenido disponible.</p>
            </div>
          ) : (
            <div className="course-content-modules">
              {course.modulos.map((modulo, moduleIndex) => {
                const moduleId = modulo.id || moduleIndex;
                const isExpanded = expandedModules[moduleId] || false;
                const moduleClasses = modulo.clases || modulo.clases || [];
                const moduleMinutes = moduleClasses.reduce((sum, clase) => sum + (clase.minutes || 0), 0);
                const moduleHours = Math.round((moduleMinutes / 60) * 10) / 10;
                const completedInModule = moduleClasses.filter((clase) =>
                  isClassCompleted(clase.id)
                ).length;

                return (
                  <div key={moduleId} className="course-content-module">
                    <div
                      className="course-content-module-header"
                      onClick={() => toggleModule(moduleId)}
                    >
                      <div className="module-header-left">
                        <i
                          className={`fas fa-chevron-${isExpanded ? 'down' : 'right'} module-chevron`}
                        ></i>
                        <i className="fas fa-folder module-icon"></i>
                        <div className="module-title-info">
                          <h3 className="module-title">
                            {modulo.nombre || modulo.title || `Módulo ${moduleIndex + 1}`}
                          </h3>
                          <span className="module-meta">
                            {moduleClasses.length} {moduleClasses.length === 1 ? 'clase' : 'clases'} • {moduleHours}h
                            {progress && (
                              <span className="module-progress">
                                {' • '}
                                {completedInModule}/{moduleClasses.length} completadas
                              </span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="course-content-module-content">
                        {modulo.descripcionBreve && (
                          <p className="module-description">{modulo.descripcionBreve}</p>
                        )}

                        {moduleClasses.length > 0 ? (
                          <div className="module-classes-list">
                            {moduleClasses
                              .sort((a, b) => (a.orden || 0) - (b.orden || 0))
                              .map((clase, classIndex) => {
                                const isCompleted = isClassCompleted(clase.id);
                                return (
                                  <div
                                    key={clase.id || classIndex}
                                    className={`module-class-item ${isCompleted ? 'completed' : ''}`}
                                    onClick={() => handleClassClick(clase)}
                                  >
                                    <div className="class-item-icon">
                                      {isCompleted ? (
                                        <i className="fas fa-check-circle text-success"></i>
                                      ) : (
                                        <i className="fas fa-play-circle"></i>
                                      )}
                                    </div>
                                    <div className="class-item-content">
                                      <h4 className="class-item-title">
                                        {clase.nombre || clase.title || `Clase ${classIndex + 1}`}
                                      </h4>
                                      {clase.descripcionBreve && (
                                        <p className="class-item-description">{clase.descripcionBreve}</p>
                                      )}
                                      <div className="class-item-meta">
                                        {clase.minutes > 0 && (
                                          <span className="class-duration">
                                            <i className="fas fa-clock me-1"></i>
                                            {clase.minutes} min
                                          </span>
                                        )}
                                        {isCompleted && (
                                          <span className="class-completed-badge">
                                            <i className="fas fa-check me-1"></i>
                                            Completada
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                          </div>
                        ) : (
                          <div className="module-empty">
                            <p>Este módulo aún no tiene clases.</p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default MyCourseContentPage;

